import React, { useState, useEffect } from "react";
import { Button, Flex, Heading } from "@nubeteck/components";
import {
  Form,
  Spin,
  Skeleton,
  Layout,
  Menu,
  Typography,
  Divider,
} from "antd";
import type { MenuProps } from "antd";
import ConfigurationInput from "./settingsInput";
import { 
  useGetGeneralSettingsMutation, 
  useGetGeneralSettingsTranslationsMutation,
  useSaveGeneralSettingsMutation 
} from "src/services/generalSettingsServices";
import { EDITOR, SHORT_TEXT } from "src/constants";
import {
  useErrorSavingDataToast,
  useErrorSelectTemplateToast,
  useErrorFieldNotFoundToast,
  useSuccessTranslationsSavedToast,
} from "src/constants";
import { getFormData } from "src/utils/files-util";
import { ISettingsSection, ISettingsField } from "src/Interfaces/generalSettings";
import { getSettingsPage } from "../../../Api/content";
import { StrapiError } from "../../StrapiError";
import { useLocale } from "src/i18n/localeContext";
const { Sider, Content } = Layout;
const { Title } = Typography;
//const { Text } = Typography;

interface GeneralTemplatesTranslationProps {
  onBack: () => void;
}
  const panelStyle: React.CSSProperties = {
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: 12,
    height: "calc(100vh - 210px)",
    overflow: "auto",
    // height: "100%",
    // overflow: "hidden",
    background: "#fff",
  };

  
  function projectFieldForLocale(
    base: ISettingsField | undefined,
    target: "es" | "en",
    appLocale: "es" | "en"
  ): ISettingsField | undefined {
    if (!base) return undefined;    
    const f: ISettingsField = { ...base };
  
    // Título visible (encabezado de panel y label de Form.Item si lo necesitas)
    if (target === "en") {
      f.label = base.clave ?? base.label; // usa título EN si existe
    } else {
      f.label = base.label;
    }
  
    // ConfigurationInput usa (locale === 'en' ? value : valor).
    // Para asegurar que siempre muestre el idioma objetivo, movemos el valor objetivo
    // al slot que ConfigurationInput leerá según el locale de la app.
    if (target === "en") {
      // Queremos mostrar inglés
      if (appLocale === "en") {
        // App en inglés -> ConfigurationInput leerá f.value, así que ponemos el inglés ahí
        f.value = base.value ?? base.valor ?? "";
        // Limpiamos valor para evitar confusión
        f.valor = "";
      } else {
        // App en español -> ConfigurationInput leerá f.valor, así que ponemos el inglés ahí
        f.valor = base.value ?? base.valor ?? "";
        // Limpiamos value para evitar confusión
        f.value = "";
      }
    } else {
      // Queremos mostrar español
      if (appLocale === "en") {
        // App en inglés -> ConfigurationInput leerá f.value, así que ponemos el español ahí
        f.value = base.valor ?? base.value ?? "";
        // Limpiamos valor para evitar confusión
        f.valor = "";
      } else {
        // App en español -> ConfigurationInput leerá f.valor, así que ponemos el español ahí
        f.valor = base.valor ?? base.value ?? "";
        // Limpiamos value para evitar confusión
        f.value = "";
      }
    }
    return f;
  }
  
  const GeneralTemplatesTranslation: React.FC<GeneralTemplatesTranslationProps> = ({ onBack }) => {
    const { locale } = useLocale(); // 'es' | 'en'
    const appLocale = (locale as "es" | "en") ?? "es";
    const [settingsPage, setSettingsPage] = useState<any>(null);
    const [isLoadingContent, setIsLoadingContent] = useState<boolean>(true);
    const [hasError, setHasError] = useState(false);
    const showErrorSavingToast = useErrorSavingDataToast();
    const showSuccessTranslationsSavedToast = useSuccessTranslationsSavedToast();
    const showErrorSelectTemplateToast = useErrorSelectTemplateToast();
    const showErrorFieldNotFoundToast = useErrorFieldNotFoundToast();

    useEffect(() => {
      let cancel = false;
      setHasError(false);
      setIsLoadingContent(true);
      
      const timeoutId = setTimeout(() => {
        if (!cancel) {
          setSettingsPage(null);
          setIsLoadingContent(false);
          setHasError(locale === 'en');
        }
      }, 3000);
      
      getSettingsPage(locale)
        .then((res) => {
          const data = res?.data?.attributes || res?.data || res;
          if (!cancel) {
            clearTimeout(timeoutId);
            setSettingsPage(data);
            setIsLoadingContent(false);
          }
        })
        .catch(() => {
          if (!cancel) {
            clearTimeout(timeoutId);
            setSettingsPage(null);
            setIsLoadingContent(false);
            setHasError(locale === 'en');
          }
        });
      return () => {
        cancel = true;
        clearTimeout(timeoutId);
      };
    }, [locale]);

    // o para el Sider
    const siderTranslations = {
      es: {
        templates: "Plantillas",
        templatesTitle: "Plantillas",
      },
      en: {
        templates: "Templates",
        templatesTitle: "Templates",
      },
    };

    const [menuOpenKeys, setMenuOpenKeys] = React.useState<string[]>([]);
    const [selectedKey, setSelectedKey] = React.useState<string>("");
    const [bootLoading, setBootLoading] = React.useState(true);
  
    const [formES] = Form.useForm();
    const [formEN] = Form.useForm();
  
    // Observar cambios en los valores del formulario para forzar re-render
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const formESValues = Form.useWatch([], formES);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const formENValues = Form.useWatch([], formEN);
  
    // Endpoint para obtener la lista inicial de campos (para el menú)
    const [getSettings, { data, isLoading: isLoadingSettings, isError }] =
      useGetGeneralSettingsMutation();
    
    // Endpoint para obtener los datos completos y traducciones de un campo específico
    const [getFieldTranslations, { 
      data: fieldTranslationData, 
      isLoading: isLoadingFieldTranslations 
    }] = useGetGeneralSettingsTranslationsMutation();
  
    const [
      saveSettings,
      { isLoading: isSaving, isSuccess: isSuccessSaving, isError: isErrorSaving },
    ] = useSaveGeneralSettingsMutation();
  
    // Carga inicial - obtener lista de campos para el menú
    React.useEffect(() => {
      if (!isLoadingContent) {
        getSettings();
      }
    }, [getSettings, isLoadingContent]);

    React.useEffect(() => {
      if (data) {
        setBootLoading(false);
      }
    }, [data]);
  
    // Normaliza lista de plantillas (flatten secciones->campos y mapa rápido)
    const { templates, byKey } = React.useMemo(() => {
      // Verificar si data.data es un array (estructura esperada)
      const dataArray = Array.isArray(data?.data) ? data.data : [];
      const sections = dataArray as ISettingsSection[];
      const tmp: ISettingsField[] = [];
      const map: Record<string, ISettingsField> = {};
      sections.forEach((s) => {
        if (s?.configuraciones && Array.isArray(s.configuraciones)) {
          s.configuraciones.forEach((f) => {
            tmp.push(f);
            map[f.label] = f;
          });
        }
      });
      return { templates: tmp, byKey: map };
    }, [data?.data]);
    
    // Cuando se selecciona un campo, obtener sus datos completos y traducciones
    React.useEffect(() => {
      if (!selectedKey) return;
      const field = byKey[selectedKey];
      if (!field || !field.id) return;
      
      // Llamar al endpoint GetTranslationsByKey con el id del campo seleccionado
      getFieldTranslations(field.id);
    }, [selectedKey, byKey, getFieldTranslations]);
  
    // Sidebar
    const menuItems: MenuProps["items"] = React.useMemo(() => {
      // Verificar si data.data es un array (estructura esperada)
      const dataArray = Array.isArray(data?.data) ? data.data : [];
      const sections = dataArray as ISettingsSection[];
      const items = sections.map((section) => ({
        key: String(section.id),
        label: section.seccion || settingsPage?.template__nb || "Plantillas",
        children: (section.configuraciones && Array.isArray(section.configuraciones))
          ? section.configuraciones.map((f) => ({
              key: f.label,
              label: appLocale === "en"
                ? (f.clave ? f.clave.replaceAll("_", " ") : f.label.replaceAll("_", " "))
                : f.label.replaceAll("_", " "),
            }))
          : [],
        type: "group" as const,
      }));
      if (items.length && menuOpenKeys.length === 0) {
        setMenuOpenKeys([String(items[0].key)]);
      }
      if (templates.length && !selectedKey) {
        setSelectedKey(templates[0].label);
      }
      return items;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data?.data, templates.length, appLocale]);
  
    // Transformar la respuesta del endpoint GetTranslationsByKey a ISettingsField
    const transformedFieldData = React.useMemo(() => {
      if (!fieldTranslationData?.data?.data) return null;
      
      const apiData = fieldTranslationData.data.data;
      const translations = fieldTranslationData.data.Translations || [];
      const translateFields = fieldTranslationData.data["translate-fields"] || [];
      
      // Buscar la traducción del campo "Valor" 
      // Las traducciones pueden tener diferentes claves, buscar la que corresponda al campo "Valor"
      // Generalmente la traducción tiene una clave relacionada con el nombre del campo en inglés
      let valorTranslation = null;
      let campoTranslation = null;
      
      // Intentar encontrar la traducción más reciente (ordenar por Id descendente)
      if (translations.length > 0) {
        const sortedTranslations = [...translations].sort((a: any, b: any) => 
          (b.TraduccionId || b.Id) - (a.TraduccionId || a.Id)
        );
        // Tomar la traducción más reciente que tenga un Valor (para el campo "Valor")
        valorTranslation = sortedTranslations.find((t: any) => t.Valor) || sortedTranslations[0];
        // Buscar la traducción del campo "Campo" (la Clave en Translations es la traducción del campo "Campo")
        campoTranslation = sortedTranslations.find((t: any) => t.Clave) || sortedTranslations[0];
      }
      
      // Determinar el tipo de campo basado en translate-fields
      const valorField = translateFields.find((f: any) => f.Columna === "Valor");
      let tipo = SHORT_TEXT;
      if (valorField?.TipoCampoNombre === "Editor") {
        tipo = EDITOR;
      } else if (valorField?.TipoCampoNombre === "Texto Corto") {
        tipo = SHORT_TEXT;
      }
      
      // Crear un ISettingsField a partir de los datos del API
      const field: ISettingsField = {
        id: apiData.ConfiguracionId,
        label: apiData.Campo,
        nombre: apiData.Clave || apiData.Campo,
        descripcion: apiData.Descripcion || "",
        tipo: tipo,
        requerido: apiData.Requerido || false,
        valor: apiData.Valor || "",
        value: valorTranslation?.Valor || "",
        // Usar la Clave de Translations (traducción del campo "Campo" en inglés) en lugar de data.Clave
        clave: campoTranslation?.Clave || apiData.Clave,
      };
      
      return field;
    }, [fieldTranslationData]);

    // Form values iniciales por selección - usar datos del endpoint GetTranslationsByKey
    React.useEffect(() => {
      if (!selectedKey) return;
      
      // Si tenemos datos transformados del endpoint GetTranslationsByKey, usarlos
      if (transformedFieldData) {
        const esValue = transformedFieldData.valor ?? "";
        const enValue = transformedFieldData.value ?? "";
        
        // Establecer valores en los formularios
        formES.setFieldValue(transformedFieldData.nombre, esValue);
        formEN.setFieldValue(`${selectedKey}_en`, enValue);
        
        // Forzar actualización del formulario
        formES.setFieldsValue({ [transformedFieldData.nombre]: esValue });
        formEN.setFieldsValue({ [`${selectedKey}_en`]: enValue });
        return;
      }
      
      // Fallback: usar datos del campo original si no hay datos transformados aún
      const field = byKey[selectedKey];
      if (!field) return;

      const esValue = field.valor ?? "";
      const enValue = field.value ?? "";
      
      formES.setFieldValue(field.nombre, esValue);
      formEN.setFieldValue(`${selectedKey}_en`, enValue);
      
      // Forzar actualización del formulario
      formES.setFieldsValue({ [field.nombre]: esValue });
      formEN.setFieldsValue({ [`${selectedKey}_en`]: enValue });
    }, [selectedKey, byKey, formES, formEN, transformedFieldData]);

    // Manejar éxito y error al guardar
    React.useEffect(() => {
      if (isErrorSaving) {
        showErrorSavingToast();
      }

      if (isSuccessSaving) {
        showSuccessTranslationsSavedToast();
        getSettings(); // Recargar datos después de guardar
      }
    }, [isSuccessSaving, isErrorSaving, getSettings, showErrorSavingToast, showSuccessTranslationsSavedToast]);
  
    // Encabezados dinámicos y campos proyectados para cada panel
    // Usar datos transformados del endpoint GetTranslationsByKey si están disponibles
    const selectedField = transformedFieldData || byKey[selectedKey];
    const fieldForES = projectFieldForLocale(selectedField, "es", appLocale);
    const fieldForEN = projectFieldForLocale(selectedField, "en", appLocale);
  
    const titleES = fieldForES?.label ?? "—";
    const titleEN = fieldForEN?.label ?? "—";
  
    const handleSave = () => {
      if (!selectedKey) {
        showErrorSelectTemplateToast();
        return;
      }
      const field = byKey[selectedKey];
      if (!field) {
        showErrorFieldNotFoundToast();
        return;
      }

      // Obtener valores de ambos formularios (español e inglés)
      const esVal = formES.getFieldValue(field.nombre) ?? "";
      const enVal = formEN.getFieldValue(`${selectedKey}_en`) ?? field.value ?? "";

      // Construir FormData con la estructura que espera el backend
      // Enviar tanto valor (español) como value (inglés)
      // El backend espera campos con sufijos: [field.nombre]-ES para español, [field.nombre]-EN para inglés
      const formDataObj: any = {
        [`${field.nombre}-ES`]: esVal,
        [`${field.nombre}-EN`]: enVal,
      };

      // Usar getFormData igual que en generalSettings.tsx
      saveSettings(getFormData(formDataObj));
    };

    useEffect(() => {
      const style = document.createElement("style");
      style.innerHTML = `
        .custom-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scroll::-webkit-scrollbar-thumb {
          background-color: #bdbdbd;
          border-radius: 4px;
        }
        .custom-scroll::-webkit-scrollbar-thumb:hover {
          background-color: #9e9e9e;
        }
        .custom-scroll::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        .custom-scroll {
          scrollbar-width: thin;
          scrollbar-color: #bdbdbd #f1f1f1;
        }
      `;
      document.head.appendChild(style);
      return () => {
        document.head.removeChild(style);
      };
    }, []);
    
  
    if (isError) {
      return (
        <Flex style={{ width: "100%", flexDirection: "column", gap: 12 }}>
          <Heading level={4}>{settingsPage?.translate_template.error_message || "No se pudieron cargar las plantillas"}</Heading>
          <div style={{ display: "flex", gap: 8 }}>
            <Button onClick={() => getSettings()}>{settingsPage?.translate_template.retry_button || "Reintentar"}</Button>
            <Button onClick={onBack}>{settingsPage?.translate_template.back_button || "Retroceder"}</Button>
          </div>
        </Flex>
      );
    }

    if (hasError && locale === 'en') {
      return <StrapiError
        onRetry={() => {
          setHasError(false);
          setIsLoadingContent(true);
        }}
        onUseDefault={() => {
          setHasError(false);
          setIsLoadingContent(false);
          setSettingsPage(null);
        }}
      />;
    }
  
    return (
      <Flex style={{ width: "100%", flexDirection: "column", gap: 16, maxWidth: 1440 }}>
        {isLoadingContent ? (
          <>
            <Skeleton.Input active size="large" style={{ width: 300, marginBottom: 16 }} />
            <Skeleton active paragraph={{ rows: 10 }} />
          </>
        ) : (
          <>
            {/* Barra superior */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Heading level={4}>{settingsPage?.translate_template.title || "Traducción de Configuraciones Generales"}</Heading>
            </div>

            {/* Skeleton inicial */}
            {bootLoading ? (
              <>
                <Skeleton.Input active size="large" style={{ width: 300, marginBottom: 16 }} />
                <Skeleton active paragraph={{ rows: 10 }} />
              </>
            ) : (
              <Layout style={{ background: "transparent", gap: 16 }}>
                {/* Sidebar */}
                <Sider
                  width={280}
                  className="custom-scroll"
                  style={{
                    background: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: 10,
                    padding: 12,
                    height: "calc(100vh - 210px)",
                    overflow: "auto",
                    // height: "100%",
                    // overflow: "hidden",
                  }}
                >
                  <Title level={5} style={{ marginTop: 6, marginBottom: 8 }}>
                    {settingsPage?.template__nb || "Plantillas"}
                  </Title>
                  <Menu
                    mode="inline"
                    selectedKeys={[selectedKey]}
                    openKeys={menuOpenKeys}
                    onOpenChange={(keys) => setMenuOpenKeys(keys as string[])}
                    onClick={(e) => setSelectedKey(e.key)}
                    items={menuItems}
                    style={{ border: "none" }}
                  />
                </Sider>

                {/* Panel central: Español (izquierda) */}
                <Content className="custom-scroll" style={panelStyle}>
                  {/* Encabezado dinámico = título de la plantilla (ES) */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <Title level={5} style={{ margin: 0 }}>{titleES}</Title>
                      {/* <Text type="secondary">{selectedKey || "—"}</Text> */}
                    </div>
                  </div>
                  <Divider style={{ margin: "12px 0" }} />

                  <Spin spinning={isLoadingSettings || isLoadingFieldTranslations}>
                    <Form form={formES} layout="vertical">
                      {/* El Form.Item usa field.nombre igual que en generalSettings.tsx */}
                      {fieldForES && selectedField ? (
                        <Form.Item
                          name={selectedField.nombre}
                          // label={fieldForES?.descripcion || "Contenido (ES)"}
                          rules={[{ required: false }]}
                        >
                          <ConfigurationInput
                            data={fieldForES}
                            onChange={(val) => formES.setFieldValue(selectedField.nombre, val)}
                            readOnly={true}
                            disabled={true}
                          />
                        </Form.Item>
                      ) : null}
                    </Form>
                  </Spin>
                </Content>

                {/* Panel derecho: Inglés */}
                <Content className="custom-scroll" style={panelStyle}>
                  {/* Encabezado dinámico = título de la plantilla (EN) */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <Title level={5} style={{ margin: 0 }}>{titleEN}</Title>
                      {/* <Text type="secondary">{selectedKey || "—"}</Text> */}
                    </div>
                  </div>
                  <Divider style={{ margin: "12px 0" }} />

                  <Spin spinning={isLoadingSettings || isLoadingFieldTranslations}>
                    <Form form={formEN} layout="vertical">
                      <Form.Item
                        name={`${selectedKey}_en`}
                        // label={fieldForEN?.descripcion || "Content (EN)"}
                        rules={[{ required: false }]}
                      >
                        {fieldForEN ? (
                          <ConfigurationInput
                            data={fieldForEN}
                            onChange={(val) => formEN.setFieldValue(`${selectedKey}_en`, val)}
                          />
                        ) : null}
                      </Form.Item>
                    </Form>
                  </Spin>
                </Content>
              </Layout>
            )}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <Button onClick={onBack}>{settingsPage?.translate_template.back_button || "Retroceder"}</Button>
                <Button type="primary" onClick={handleSave} loading={isSaving}>
                  {settingsPage?.translate_template.save_button || "Guardar"}
                </Button>
              </div>
          </>
        )}
      </Flex>
    );
  };
  
  export default GeneralTemplatesTranslation;